import { useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchUpdates } from 'react-dom';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import useFeedbackAttachments from './useFeedbackAttachments';

import useAnalytics from 'core/hooks/useAnalytics';

import { EVENT_NAMES } from '../../../constants/eventNames';
import feedbackService from '../../../services/feedbackService';
import type { FeedbackCategory } from '../../../services/feedbackService';

/**
 * useFeedbackModal - Owns the feedback form state (category + comment), the
 * submission against the feedback service and the success/error status dialog.
 * On success the form is reset and closing the status dialog closes the modal;
 * on error the text is kept so the user can retry.
 */

export const COMMENT_MAX_LENGTH = 1000;
/** The bar's visual resolution is far below 1 %, and every tick re-renders the whole dialog. */
const UPLOAD_PROGRESS_STEP = 5;
const DEFAULT_CATEGORY: FeedbackCategory = 'SUGGESTION';
const FEEDBACK_CATEGORIES: readonly FeedbackCategory[] = ['SUGGESTION', 'ERROR', 'OTHER'];

export type FeedbackCategoryOption = {
  label: string;
  value: FeedbackCategory;
};

type FeedbackStatus = 'success' | 'error' | null;
const STATUS_MESSAGES = {
  success: { title: 'feedback_success_title', description: 'feedback_success_description' },
  error: { title: 'feedback_error_title', description: 'feedback_error_description' },
};

type UseFeedbackModalParams = {
  isOpen: boolean;
  onClose: () => void;
};

type UseFeedbackModalReturn = ReturnType<typeof useFeedbackAttachments> & {
  uploadProgress: number;
  uploadStatus: string;
  cancelLabel: string;
  categoryLabel: string;
  categoryOptions: FeedbackCategoryOption[];
  comment: string;
  commentLabel: string;
  commentPlaceholder: string;
  handleCategoryChange: (option: unknown) => void;
  handleClose: () => void;
  handleCommentChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  handleStatusClose: () => void;
  handleSubmit: () => void;
  isFormVisible: boolean;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  selectedOption: FeedbackCategoryOption | null;
  status: FeedbackStatus;
  statusDescription: string;
  statusTitle: string;
  submitLabel: string;
  subtitle: string;
  title: string;
};

function isFeedbackCategory(value: unknown): value is FeedbackCategory {
  return typeof value === 'string' && (FEEDBACK_CATEGORIES as readonly string[]).includes(value);
}

function clampComment(value: string): string {
  if (value.length <= COMMENT_MAX_LENGTH) return value;

  const sliced = value.slice(0, COMMENT_MAX_LENGTH);
  const lastCode = sliced.charCodeAt(sliced.length - 1);
  const endsInHighSurrogate = lastCode >= 0xd800 && lastCode <= 0xdbff;

  return endsInHighSurrogate ? sliced.slice(0, -1) : sliced;
}

function useFeedbackModal({ isOpen, onClose }: UseFeedbackModalParams): UseFeedbackModalReturn {
  const { t } = useTranslation(['chatbot', 'actions', 'common']);
  const { trackEvent } = useAnalytics();

  const [category, setCategory] = useState<FeedbackCategory>(DEFAULT_CATEGORY);
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [status, setStatus] = useState<FeedbackStatus>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const isSubmittingRef = useRef(false);
  const attachmentState = useFeedbackAttachments({ isOpen, isSubmitting });
  const abortControllerRef = useRef<AbortController | null>(null);

  // The modal stays mounted while hidden, so a submission settling after a
  // mid-flight cancel must not leak into the next open: every open/close
  // transition aborts the in-flight request (otherwise reopening would allow
  // a second submission while the first can still persist a duplicate) and
  // invalidates its settlement; opening resets the form.
  useEffect(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    if (!isOpen) return;

    setCategory(DEFAULT_CATEGORY);
    setComment('');
    setStatus(null);
    setIsSubmitting(false);
    isSubmittingRef.current = false;
    setUploadProgress(0);
  }, [isOpen]);

  // Nothing must keep running against an unmounted modal.
  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const categoryOptions = useMemo<FeedbackCategoryOption[]>(
    () => [
      { label: t('chatbot:feedback_category_suggestion'), value: 'SUGGESTION' },
      { label: t('chatbot:feedback_category_error'), value: 'ERROR' },
      { label: t('chatbot:feedback_category_other'), value: 'OTHER' },
    ],
    [t]
  );

  const selectedOption = categoryOptions.find((option) => option.value === category) ?? null;
  const statusMessages = STATUS_MESSAGES[status ?? 'error'];

  function resetForm(): void {
    setCategory(DEFAULT_CATEGORY);
    setComment('');
    attachmentState.resetAttachments();
  }

  function handleCategoryChange(option: unknown): void {
    const value = (option as FeedbackCategoryOption | null)?.value;
    if (!isFeedbackCategory(value)) return;

    setCategory(value);
  }

  function handleCommentChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    setComment(clampComment(event.target.value));
  }

  function trackSubmission(): void {
    Promise.resolve()
      .then(() => trackEvent(EVENT_NAMES.USER_FEEDBACK_SUBMITTED, { category }))
      .catch((analyticsError) => {
        console.error('Failed to track user feedback submitted event:', analyticsError);
      });
  }

  async function handleSubmit(): Promise<void> {
    const trimmedComment = comment.trim();
    if (!trimmedComment || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setUploadProgress(0);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsSubmitting(true);

    // Every open/close transition aborts the request, so an aborted signal marks a stale submission.
    function handleUploadProgress(progress: number): void {
      const step = progress === 100 ? 100 : Math.floor(progress / UPLOAD_PROGRESS_STEP) * UPLOAD_PROGRESS_STEP;
      if (!abortController.signal.aborted) setUploadProgress(step);
    }

    // React 17 does not batch updates made after an await: without this each setter re-renders the dialog.
    function settle(update: () => void): void {
      if (abortController.signal.aborted) return;
      batchUpdates(() => {
        update();
        setIsSubmitting(false);
        isSubmittingRef.current = false;
      });
    }

    try {
      await feedbackService.submitFeedback(
        { category, comment: trimmedComment, attachments: attachmentState.attachments },
        { signal: abortController.signal, onProgress: handleUploadProgress }
      );
      settle(() => {
        trackSubmission();
        resetForm();
        setStatus('success');
      });
    } catch (error) {
      settle(() => {
        if (error instanceof Error && attachmentState.showAttachmentError(error.message)) return;
        setStatus('error');
      });
    }
  }

  function handleClose(): void {
    resetForm();
    setStatus(null);
    onClose();
  }

  function handleStatusClose(): void {
    setStatus(null);
    if (status === 'success') onClose();
  }

  const uploadStatusKey = uploadProgress < 100 && attachmentState.attachments.length > 0 ? 'common:Uploading file' : 'chatbot:Sending...';
  const uploadStatus = isSubmitting ? t(uploadStatusKey) : '';

  return {
    ...attachmentState,
    uploadProgress,
    uploadStatus,
    cancelLabel: t('actions:Cancel'),
    categoryLabel: t('chatbot:feedback_category_label'),
    categoryOptions,
    comment,
    commentLabel: t('chatbot:feedback_comment_label'),
    commentPlaceholder: t('chatbot:feedback_comment_placeholder'),
    handleCategoryChange,
    handleClose,
    handleCommentChange,
    handleStatusClose,
    handleSubmit,
    isFormVisible: status === null,
    isSubmitDisabled: comment.trim() === '' || isSubmitting,
    isSubmitting,
    selectedOption,
    status,
    statusDescription: t(`chatbot:${statusMessages.description}`),
    statusTitle: t(`chatbot:${statusMessages.title}`),
    submitLabel: isSubmitting ? t('chatbot:Sending...') : t('actions:Send'),
    subtitle: t('chatbot:feedback_modal_subtitle'),
    title: t('chatbot:feedback_modal_title'),
  };
}

export default useFeedbackModal;
